// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const REVENUE_ENGINES = [
  { label: 'Platform Subscriptions', value: '$24,850', change: '+12 clients this month', color: '#6366f1', icon: '\uD83D\uDCCA' },
  { label: 'App Generator Revenue', value: '$145,000', change: '3 builds in progress', color: '#10b981', icon: '\u2699\uFE0F' },
  { label: 'Patent Filings', value: '$4,200', change: '21 drafts generated', color: '#f59e0b', icon: '\uD83D\uDCDC' },
  { label: 'Co-Dev Equity Deals', value: '$280,000', change: '4 active partnerships', color: '#8b5cf6', icon: '\uD83E\uDD1D' },
  { label: 'Monthly Hosting (MRR)', value: '$18,450', change: '+$3,200 vs last month', color: '#ec4899', icon: '\uD83C\uDF10' },
  { label: 'Legal Doc Revenue', value: '$3,600', change: '48 documents generated', color: '#14b8a6', icon: '\uD83D\uDCC4' },
];

const ACTIVE_BUILDS = [
  { client: 'Canstorr Commodities', industry: 'Agriculture', tier: 'Professional', progress: 85, status: 'Testing', color: '#10b981' },
  { client: 'Steve Marshall Ford', industry: 'Auto Dealership', tier: 'Enterprise', progress: 40, status: 'Building', color: '#6366f1' },
  { client: 'Filthy Redneck CC', industry: 'Event Venue', tier: 'Enterprise', progress: 15, status: 'Configuring', color: '#f59e0b' },
];

const RECENT_ACTIVITY = [
  { text: 'New patent draft generated — "Autonomous Grain Sorting System"', time: '12 min ago', icon: '\uD83D\uDCDC' },
  { text: 'App build completed — Canstorr CropPatrol deployment', time: '2 hours ago', icon: '\u2705' },
  { text: 'New idea submitted — IoT Livestock Health Monitor', time: '3 hours ago', icon: '\uD83D\uDCA1' },
  { text: 'Client Service Agreement generated — Three Way Industries', time: '5 hours ago', icon: '\uD83D\uDCC4' },
  { text: 'Co-dev deal signed — 65/35 equity split — MediTrack Pro', time: 'Yesterday', icon: '\uD83E\uDD1D' },
  { text: 'Website deployed — CropPatrol updated on Cloudflare Pages', time: 'Yesterday', icon: '\uD83C\uDF10' },
];

const QUICK_ACTIONS = [
  { label: 'New AI Intake', icon: '\uD83E\uDDE0', path: '/ai-intake', desc: 'Interview an inventor' },
  { label: 'Generate App', icon: '\u2699\uFE0F', path: '/app-generator', desc: 'Build a new app' },
  { label: 'Draft Patent', icon: '\uD83D\uDCDC', path: '/patent-generator', desc: 'File for ~$200' },
  { label: 'Legal Doc', icon: '\uD83D\uDCC4', path: '/legal-generator', desc: 'NDA, agreement, LOI' },
  { label: 'Build Website', icon: '\uD83C\uDF10', path: '/website-builder', desc: 'Launch a site' },
  { label: 'Design Logo', icon: '\uD83C\uDFA8', path: '/logo-generator', desc: 'Brand identity' },
  { label: 'View Portfolio', icon: '\uD83D\uDCBC', path: '/portfolio', desc: '26 live apps' },
];

const PIPELINE = [
  { stage: 'Ideas Submitted', count: 47, color: '#6366f1' },
  { stage: 'Under Review', count: 12, color: '#f59e0b' },
  { stage: 'In Development', count: 8, color: '#10b981' },
  { stage: 'Patents Filed', count: 21, color: '#8b5cf6' },
  { stage: 'Apps Deployed', count: 25, color: '#ec4899' },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('month');

  return (
    <div className="page-body" style={{ padding: 24 }}>
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 className="page-title" style={{ color: 'var(--text)', fontSize: 28, fontWeight: 700, margin: '0 0 4px' }}>
            Innovation Hub
          </h1>
          <p className="page-subtitle" style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
            Welcome back, {user?.full_name?.split(' ')[0] || 'Innovator'} &mdash; Here&apos;s your command center
          </p>
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--card)', borderRadius: 8, padding: 4, border: '1px solid var(--border)' }}>
          {['week', 'month', 'quarter', 'year'].map(r => (
            <button key={r} onClick={() => setTimeRange(r)} style={{
              padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: timeRange === r ? '#6366f1' : 'transparent',
              color: timeRange === r ? '#fff' : 'var(--text-muted)',
              textTransform: 'capitalize'
            }}>{r}</button>
          ))}
        </div>
      </div>

      {/* Revenue Engine Cards */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 14, marginBottom: 24
      }}>
        {REVENUE_ENGINES.map((s, i) => (
          <div key={i} style={{
            padding: 18, background: 'var(--card)', borderRadius: 12,
            border: '1px solid var(--border)', position: 'relative', overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 3, background: s.color }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {s.label}
              </span>
              <span style={{ fontSize: 18 }}>{s.icon}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color, marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.change}</div>
          </div>
        ))}
      </div>

      {/* Innovation Pipeline */}
      <div style={{
        background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)',
        padding: 20, marginBottom: 24
      }}>
        <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>Innovation Pipeline</h2>
        <div style={{ display: 'flex', gap: 0, alignItems: 'center' }}>
          {PIPELINE.map((p, i) => (
            <React.Fragment key={p.stage}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%', background: `${p.color}20`,
                  border: `3px solid ${p.color}`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', margin: '0 auto 8px', fontSize: 20, fontWeight: 800, color: p.color
                }}>{p.count}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{p.stage}</div>
              </div>
              {i < PIPELINE.length - 1 && (
                <div style={{ width: 40, height: 2, background: 'var(--border)', flexShrink: 0 }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {/* Active Builds */}
        <div style={{
          background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)', padding: 20
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, margin: 0 }}>Active App Builds</h2>
            <button onClick={() => navigate('/app-generator')} style={{
              padding: '4px 12px', background: 'rgba(99,102,241,0.1)', color: '#6366f1',
              border: '1px solid rgba(99,102,241,0.2)', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer'
            }}>View All</button>
          </div>
          {ACTIVE_BUILDS.map((build, i) => (
            <div key={i} style={{
              padding: 14, background: 'var(--bg)', borderRadius: 10, marginBottom: i < ACTIVE_BUILDS.length - 1 ? 10 : 0,
              border: '1px solid var(--border)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div>
                  <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>{build.client}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{build.industry} &middot; {build.tier}</div>
                </div>
                <span style={{
                  padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700,
                  background: `${build.color}20`, color: build.color, textTransform: 'uppercase'
                }}>{build.status}</span>
              </div>
              <div style={{ width: '100%', height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: `${build.progress}%`, height: '100%', background: build.color,
                  borderRadius: 3, transition: 'width 0.5s ease'
                }} />
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, textAlign: 'right' }}>{build.progress}% complete</div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div style={{
          background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)', padding: 20
        }}>
          <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>Quick Actions</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {QUICK_ACTIONS.map((qa, i) => (
              <button key={i} onClick={() => navigate(qa.path)} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                padding: '16px 12px', background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center'
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.background = 'rgba(99,102,241,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg)'; }}
              >
                <span style={{ fontSize: 24 }}>{qa.icon}</span>
                <span style={{ color: 'var(--text)', fontSize: 12, fontWeight: 600 }}>{qa.label}</span>
                <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>{qa.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{
        background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)', padding: 20
      }}>
        <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>Recent Activity</h2>
        {RECENT_ACTIVITY.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
            borderBottom: i < RECENT_ACTIVITY.length - 1 ? '1px solid var(--border)' : 'none'
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text)', fontSize: 13 }}>{item.text}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>{item.time}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom Stats Bar */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginTop: 24,
        padding: 16, background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)'
      }}>
        {[
          { label: 'Total Revenue (YTD)', value: '$476,100', color: '#10b981' },
          { label: 'Active Clients', value: '47', color: '#6366f1' },
          { label: 'Apps Deployed', value: '25', color: '#8b5cf6' },
          { label: 'Patents Filed', value: '21', color: '#f59e0b' },
        ].map((s, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
